import { useEffect } from 'react';
import { useCountries, useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { Select } from '@/components/ui/select';

interface AddressSelectorProps {
  countryId?: number;
  provinceId?: number;
  districtId?: number;
  wardId?: number;
  onCountryChange?: (id: number) => void;
  onProvinceChange?: (id: number) => void;
  onDistrictChange?: (id: number) => void;
  onWardChange?: (id: number) => void;
  errors?: {
    countryId?: string;
    provinceId?: string;
    districtId?: string;
    wardId?: string;
  };
  hideCountry?: boolean;
  hideProvince?: boolean;
  hideDistrict?: boolean;
  hideWard?: boolean;

  // Required labels
  required?: boolean;
}

export default function AddressSelector({
  countryId,
  provinceId,
  districtId,
  wardId,
  onCountryChange,
  onProvinceChange,
  onDistrictChange,
  onWardChange,
  errors,
  hideCountry = false,
  hideProvince = false,
  hideDistrict = false,
  hideWard = false,
  required = false,
}: AddressSelectorProps) {
  const { data: countries = [], isLoading: loadingCountries } = useCountries();
  const { data: provinces = [], isLoading: loadingProvinces } = useProvinces(countryId);
  const { data: districts = [], isLoading: loadingDistricts } = useDistricts(provinceId);
  const { data: wards = [], isLoading: loadingWards } = useWards(districtId);

  const toOpts = (arr: { id: number; name: string }[]) => arr.map((i) => ({ value: String(i.id), label: i.name }));

  const req = required ? ' *' : '';

  const formatError = (err?: string, fieldName?: string) => {
    if (!err) return undefined;
    if (err.toLowerCase().includes('expected number') || err.toLowerCase().includes('received nan') || err.toLowerCase().includes('expected string')) {
      return `Vui lòng chọn ${fieldName}`;
    }
    return err;
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Quốc gia */}
      {!hideCountry && (
        <Select
          label={`Quốc gia${req}`}
          placeholder={loadingCountries ? 'Đang tải...' : '-- Chọn quốc gia --'}
          options={toOpts(countries)}
          value={countryId ? String(countryId) : ''}
          onChange={(e) => onCountryChange?.(Number(e.target.value) || 0)}
          error={formatError(errors?.countryId, 'quốc gia')}
          disabled={loadingCountries}
        />
      )}

      {/* Tỉnh / Thành phố */}
      {!hideProvince && (
        <Select
          label={`Tỉnh / Thành phố${req}`}
          placeholder={!countryId ? 'Chọn quốc gia trước' : loadingProvinces ? 'Đang tải...' : provinces.length === 0 ? 'Không có dữ liệu' : '-- Chọn tỉnh/thành --'}
          options={toOpts(provinces)}
          value={provinceId ? String(provinceId) : ''}
          onChange={(e) => onProvinceChange?.(Number(e.target.value) || 0)}
          error={formatError(errors?.provinceId, 'tỉnh/thành phố')}
          disabled={!countryId || loadingProvinces}
        />
      )}

      {/* Quận / Huyện */}
      {!hideDistrict && (
        <Select
          label={`Quận / Huyện${req}`}
          placeholder={!provinceId ? 'Chọn tỉnh/thành trước' : loadingDistricts ? 'Đang tải...' : districts.length === 0 ? 'Không có dữ liệu' : '-- Chọn quận/huyện --'}
          options={toOpts(districts)}
          value={districtId ? String(districtId) : ''}
          onChange={(e) => onDistrictChange?.(Number(e.target.value) || 0)}
          error={formatError(errors?.districtId, 'quận/huyện')}
          disabled={!provinceId || loadingDistricts}
        />
      )}

      {/* Phường / Xã */}
      {!hideWard && (
        <Select
          label={`Phường / Xã${req}`}
          placeholder={!districtId ? 'Chọn quận/huyện trước' : loadingWards ? 'Đang tải...' : wards.length === 0 ? 'Không có dữ liệu' : '-- Chọn phường/xã --'}
          options={toOpts(wards)}
          value={wardId ? String(wardId) : ''}
          onChange={(e) => onWardChange?.(Number(e.target.value) || 0)}
          error={formatError(errors?.wardId, 'phường/xã')}
          disabled={!districtId || loadingWards}
        />
      )}
    </div>
  );
}
